import { Button, Dialog } from "@hisani/ui";
import { useMutation } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import React, { type FormEvent } from "react";
import { requestApi } from "../../api-client";
import type { MarketplacePreviewListing } from "./marketplace-sample-data";
import { MarketplaceSelectFormField, requiredText } from "./marketplace-form";
import {
  MARKETPLACE_REPORT_REASONS,
  MARKETPLACE_REPORT_REASON_MESSAGE_KEYS,
} from "./report-reasons";

export function ReportDialog({
  listing,
  open,
  onClose,
  onReported,
}: {
  readonly listing: MarketplacePreviewListing | null;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onReported: () => void;
}) {
  const t = useTranslations("Marketplace");
  const reportMutation = useMutation({
    mutationFn: ({ listingId, reason, details }: ReportVariables) =>
      requestApi(`/api/v1/marketplace/listings/${listingId}/reports`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          origin: window.location.origin,
        },
        body: JSON.stringify({ reason, details }),
      }),
    onSuccess: onReported,
  });
  const pending = reportMutation.isPending;
  const error =
    reportMutation.error === null
      ? null
      : reportMutation.error instanceof Error
        ? reportMutation.error.message
        : t("reportError");

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (listing === null) return;
    const form = new FormData(event.currentTarget);
    reportMutation.mutate({
      listingId: listing.id,
      reason: requiredText(form, "reason"),
      details: requiredText(form, "details"),
    });
  }

  return (
    <Dialog
      open={open}
      title={t("reportTitle")}
      description={t("reportDescription")}
      closeLabel={t("close")}
      onClose={onClose}
    >
      <form onSubmit={submit} className="grid gap-4">
        <MarketplaceSelectFormField
          name="reason"
          label={t("reportReason")}
          placeholder={t("chooseReportReason")}
          required
          options={MARKETPLACE_REPORT_REASONS.map((reason) => [
            reason,
            t(MARKETPLACE_REPORT_REASON_MESSAGE_KEYS[reason]),
          ])}
        />
        <label className="text-sm font-bold">
          {t("reportDetails")}
          <textarea
            name="details"
            required
            minLength={10}
            maxLength={2000}
            rows={4}
            className="mt-2 w-full rounded-md border border-border-default bg-surface px-3 py-2 font-normal outline-none transition-[border-color,box-shadow] focus:border-focus focus:ring-2 focus:ring-focus/20"
          />
        </label>
        {error === null ? null : (
          <p role="alert" className="text-sm font-semibold text-danger">
            {error}
          </p>
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={pending}
          >
            {t("cancel")}
          </Button>
          <Button type="submit" variant="accent" disabled={pending}>
            {pending ? t("sending") : t("sendReport")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

interface ReportVariables {
  readonly listingId: string;
  readonly reason: string;
  readonly details: string;
}
